import React, { useState, useEffect } from 'react';
import { entryInSeason, splitZmanToken, zmanTokenLabel, guessTimezone, zmanimUrl,
  holidayEntryDate, oraripreghieraNascosti, continuousRun, localDateISO } from './api';
import { t } from './i18n';

const COLORS = {
  primary: '#1a4d8f',
  primaryDark: '#0d3163',
  bg: '#f7f8fa',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  accent: '#d4af37',
};

// Day code → display label
const DAY_LABEL = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  shabbat: 'Shabbat',
  holiday: 'Holidays',
  high_holiday: 'High Holidays',
  weekday: 'Weekdays',
  rosh_chodesh: 'Rosh Chodesh',
  fast: 'Fast days',
  daily: 'Daily',
};

const DAYS_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'shabbat', 'holiday', 'rosh_chodesh', 'fast', 'high_holiday', 'weekday', 'daily'];

// Format a list of days into a friendly label
function formatDays(days) {
  if (!days || days.length === 0) return '';
  if (days.includes('daily')) return t('Daily');
  if (days.length === 1) return t(DAY_LABEL[days[0]] || days[0]);
  // Detect ranges in weekdays
  const sorted = [...days].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
  return sorted.map((d) => t(DAY_LABEL[d] || d)).join(', ');
}

// «SHABBAT» E «SABATO SERA» NON SONO LO STESSO GIORNO.
//
// Menachem, 23/10/2026: «manca Arvit di sabato sera, che e 20 minuti dopo la
// fine, perche non lo mostro?». Non mancava: c'era scritto
//
//     Arvit / Maariv    Shabbat    End of Shabbat +20 min (7:30 PM)
//
// ed e proprio l'etichetta a renderlo irriconoscibile. Quella funzione
// avviene DOPO che Shabbat e finito: chiamarla «Shabbat» la fa sembrare
// durante, e chi cerca «sabato sera» non la trova. Nell'archivio sono 21
// voci su 20 schede.
//
// Il giorno da solo non basta a deciderlo, serve l'ORARIO: `shabbat_end+20`
// e dopo, ma `shabbat_end-60` e un'ora PRIMA della fine — quella e Mincha di
// Shabbat, e «Shabbat» e giusto. Nell'archivio ce ne sono 4 cosi, e non
// vanno toccate.
//
// Si rietichetta solo quando la voce parla del solo Shabbat e TUTTI i suoi
// orari cadono alla fine o dopo. Una voce mista (Shabbat + feste) resta
// com'e: non si sa a quale dei due si riferirebbe.
// E QUANDO LA FONTE LO DICE, NON SI INDOVINA.
//
// La regola qui sotto legge il TOKEN per capire se una voce cade dopo la
// fine di Shabbat, e con `shabbat_end+20` funziona. Ma il registro di
// Afula scriveva i servizi per nome — «ערבית מוצאי שבת», «מנחה ערב שבת»
// — e il mio importatore ha buttato via il nome tenendo solo l'orario. A
// Noam Siach e' rimasto «Shabbat 17:20» per una tefilla che avviene a
// Shabbat FINITO, e con un'ora fissa nessuna euristica puo' saperlo.
//
// Percio' se una voce porta il suo nome scritto, quello vince: chi ha
// letto la fonte ne sa piu' di chi guarda il numero.
function etichettaGiorni(entry) {
  if (entry && entry.label) return t(entry.label);
  const giorni = entry && entry.days ? entry.days : [];
  const soloShabbat = giorni.length === 1 && (giorni[0] === 'shabbat' || giorni[0] === 'sat');
  if (soloShabbat) {
    const orari = (entry.times || []).map((x) => /^shabbat_end([+-]\d+)?/.exec(String(x)));
    const tuttiDopo = orari.length > 0 && orari.every((m) => m && (m[1] ? parseInt(m[1], 10) : 0) >= 0);
    if (tuttiDopo) return t('Motzaei Shabbat');
  }
  return formatDays(giorni);
}

// Format a time token to display. `z` = zmanim base times in minutes-from-
// midnight { candle_lighting, sundown, plag_hamincha } (place-local) when
// available. A token may carry an offset ("sundown-40") which is applied here.
function formatTime(time, z) {
  z = z || {};
  const zt = splitZmanToken(time);
  if (zt) {
    const label = t(zmanTokenLabel(time));
    const base = z[zt.base];
    if (base == null) {
      // Can't resolve the zman here (no location) — show the label, noting the cap.
      return zt.cap != null ? `${label} (${t('not later than')} ${fmtMinutes(zt.cap)})` : label;
    }
    const resolved = base + zt.offset;
    // "not later than" cap: once the zman falls later than the cap, the service
    // is at the fixed cap time — show just that; otherwise show the zman.
    if (zt.cap != null && zt.cap < resolved) return fmtMinutes(zt.cap);
    return `${label} (${fmtMinutes(resolved)})`;
  }
  // HH:MM → "8:00 AM" / "7:30 PM"
  const m = time.match(/^(\d{2}):(\d{2})$/);
  if (m) {
    let h = parseInt(m[1]);
    const min = m[2];
    const period = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${min} ${period}`;
  }
  return time;
}

// "Friday, 2 October" — a bare YYYY-MM-DD is parsed as UTC midnight, so it is
// formatted in UTC too and cannot slip a day west of Greenwich.
function formatHolidayDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
    }).format(d);
  } catch (_) { return iso; }
}

// Read the clock (minutes-from-midnight) off a Hebcal offset-bearing timestamp.
function isoMinutes(iso) {
  const m = /T(\d{2}):(\d{2})/.exec(String(iso || ''));
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}
function fmtMinutes(total) {
  if (total == null) return null;
  const t = ((total % 1440) + 1440) % 1440;
  let h = Math.floor(t / 60); const min = String(t % 60).padStart(2, '0');
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${min} ${period}`;
}

// Compute today's zmanim for a location via Hebcal's zmanim endpoint: sundown
// (shkia), Plag Hamincha, and candle lighting (sunset − 18 min, the common
// default). All as the place's local clock time. One reliable request.
async function fetchZmanim(lat, lng, country) {
  const out = {};
  try {
    const today = new Date().toISOString().slice(0, 10);
    // Chabad-sourced zmanim via our backend proxy (Hebcal fallback). Same shape.
    const tz = guessTimezone(country, lat, lng);
    const zr = await fetch(zmanimUrl(lat, lng, tz, today));
    if (!zr.ok) return out;
    const t = (await zr.json()).times || {};
    if (t.sunset) {
      out.sundown = isoMinutes(t.sunset);
      out.candle_lighting = isoMinutes(t.sunset) - 18;
    }
    // Netz, for a shacharit set relative to it rather than to a clock.
    if (t.sunrise) out.sunrise = isoMinutes(t.sunrise);
    if (t.plagHaMincha) out.plag_hamincha = isoMinutes(t.plagHaMincha);
    // Shabbat end (havdalah) — nightfall/3-stars if Hebcal returns it, else a
    // common sunset + 42 min fallback.
    const tzeit = isoMinutes(t.tzeit85deg) ?? isoMinutes(t.tzeit72min) ?? (t.sunset ? isoMinutes(t.sunset) + 42 : null);
    if (tzeit != null) out.shabbat_end = tzeit;
  } catch (e) { /* ignore */ }
  return out; // base times as minutes-from-midnight (place-local)
}

export default function PrayerTimes({ establishment }) {
  const [zmanim, setZmanim] = useState({});

  const pt = establishment.prayer_times;
  const hasStructured = pt && (pt.shacharit?.length > 0 || pt.mincha?.length > 0 || pt.arvit?.length > 0 || pt.kabbalat_shabbat?.length > 0);
  // Dated one-offs — Hoshana Rabba, Selichot, a Pesach schedule. Each is kept
  // only while its date is still ahead, so the section empties itself.
  const placeTz = guessTimezone(establishment.country, establishment.city);
  const holidayTimes = (Array.isArray(pt?.holiday_times) ? pt.holiday_times : [])
    .map((e) => ({ ...e, _on: holidayEntryDate(e, placeTz) }))
    .filter((e) => e._on)
    .sort((a, b) => (a._on < b._on ? -1 : a._on > b._on ? 1 : 0));

  // Any sun-based token (candle lighting / sundown / plag) that needs a
  // computed time?
  const usesZmanim = hasStructured && [pt.shacharit, pt.mincha, pt.arvit, pt.kabbalat_shabbat]
    .flat()
    .some((entry) => entry?.times?.some((t) => splitZmanToken(t)));
  // A Shabbat (Saturday) minyan or Kabbalat Shabbat → we can also show this
  // week's candle-lighting and Shabbat-end times in the header.
  const hasShabbatEntry = hasStructured && ((pt.kabbalat_shabbat || []).length > 0 || [pt.shacharit, pt.mincha, pt.arvit]
    .flat()
    .some((e) => (e?.days || []).some((d) => d === 'sat' || d === 'shabbat')));

  // Compute zmanim (candle lighting, sundown, plag, Shabbat end) for the location.
  useEffect(() => {
    if (!usesZmanim && !hasShabbatEntry) return;
    const coords = establishment.coordinates;
    if (!coords || typeof coords.x !== 'number') return;
    fetchZmanim(coords.y, coords.x, establishment.country).then(setZmanim);
  }, [usesZmanim, hasShabbatEntry, establishment.coordinates, establishment.country]);
  const candleLighting = zmanim.candle_lighting != null ? fmtMinutes(zmanim.candle_lighting) : null;
  const shabbatEnd = zmanim.shabbat_end != null ? fmtMinutes(zmanim.shabbat_end) : null;

  if (!pt) return null;
  // L'uscita sta QUI e non in cima: sopra c'e' un useEffect, e un `return`
  // prima di un hook cambia l'ordine degli hook fra un render e l'altro.
  // React lo vieta e la compilazione si ferma.
  if (oraripreghieraNascosti(establishment)) return null;
  if (!hasStructured && !pt.raw_text && holidayTimes.length === 0) return null;

  const lat = establishment.coordinates && typeof establishment.coordinates.y === 'number' ? establishment.coordinates.y : null;
  // Il sole di QUESTO posto, per le finestre dei minyanim continui. Si calcola
  // qui, senza chiedere niente alla rete: `zmanMinutes` sa fare netz, chatzot,
  // mincha gedola, shkia e tzeit da sole e coordinate.
  const lng = establishment.coordinates && typeof establishment.coordinates.x === 'number'
    ? establishment.coordinates.x : null;
  const tzQui = guessTimezone(establishment.country, lat, lng);
  const ctxSole = { lat, lng, tz: tzQui, dateISO: localDateISO(tzQui) };

  // THE OTHER SEASON IS SHOWN, NOT HIDDEN.
  //
  // Menachem, 21/10/2026, con due schermate a confronto: l'anteprima del
  // modulo diceva «Mincha: Mon–Thu 1:30 PM (winter)» e la pagina pubblica
  // non diceva niente. Qui si filtrava per stagione corrente, e il 21 ottobre
  // in Francia l'ora legale finisce il 25: per quattro giorni ancora siamo
  // «summer», e la mincha invernale spariva. La scheda sembrava un posto
  // senza minyan infrasettimanale.
  //
  // Una scheda risponde a «a che ora è mincha qui», non solo a «a che ora è
  // mincha oggi» — chi guarda a ottobre per dicembre deve vederlo. Quindi
  // l'altra stagione resta in pagina, scritta più in chiaro e dopo quella in
  // corso. Chi un minyan lo cerca adesso ha MinyanPage, e lì il filtro per
  // stagione corrente ci sta e rimane.
  const renderService = (label, allEntries) => {
    const tutte = allEntries || [];
    if (tutte.length === 0) return null;
    const entries = [...tutte].sort((a, b) =>
      (entryInSeason(b, lat) ? 1 : 0) - (entryInSeason(a, lat) ? 1 : 0));
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.primary, marginBottom: 4 }}>{t(label)}</div>
        {entries.map((entry, idx) => {
          const adesso = entryInSeason(entry, lat);
          // UNA FINESTRA, NON UN'ORA. Dove si prega ogni quarto d'ora non c'e'
          // un orario da scrivere: c'e' da quando a fino a quando, e ogni
          // quanto. L'ora dell'orologio sopra, i nomi degli zmanim sotto —
          // perche' «fino a shkia» e «fino alle 16:49» dicono la stessa cosa a
          // due persone diverse.
          const corsa = continuousRun(entry, ctxSole);
          return (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', opacity: adesso ? 1 : 0.6, borderBottom: idx < entries.length - 1 ? `1px dashed ${COLORS.border}` : 'none' }}>
              <span style={{ color: COLORS.muted, fontSize: 14 }}>
                {etichettaGiorni(entry)}
                {entry.season && <span style={{ fontSize: 11, color: adesso ? COLORS.accent : COLORS.muted, marginLeft: 6 }}>({t(entry.season)})</span>}
              </span>
              <span style={{ fontWeight: 500, fontSize: 14, color: COLORS.text, textAlign: 'right' }}>
                {corsa ? (
                  <>
                    <span style={{ fontWeight: 700 }}>
                      🔁 {corsa.every
                        ? <>{t('a minyan every')} {corsa.every} {t('min')}</>
                        : t('minyanim one after another')}
                    </span>
                    <span style={{ display: 'block', fontSize: 13, color: COLORS.text }}>
                      {corsa.start != null ? fmtMinutes(corsa.start) : t(corsa.startLabel)}
                      {' → '}
                      {corsa.end != null ? fmtMinutes(corsa.end) : t(corsa.endLabel)}
                    </span>
                    {corsa.start != null && corsa.end != null && (
                      <span style={{ display: 'block', fontSize: 11, color: COLORS.muted }}>
                        {t(corsa.startLabel)} → {t(corsa.endLabel)}
                      </span>
                    )}
                  </>
                ) : (entry.times || []).map((t, i) => (
                  <span key={i}>
                    {formatTime(t, zmanim)}
                    {i < (entry.times || []).length - 1 && <span style={{ color: COLORS.muted }}> · </span>}
                  </span>
                ))}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Fold a matching Mincha + Arvit (same days/times/season — a combined
  // "Mincha-Maariv") into one section instead of listing it under both.
  // keyOf include la stagione, quindi una mincha d'estate e un arvit
  // d'inverno non si fondono per sbaglio ora che non si filtra più.
  // LA FINESTRA FA PARTE DELLA CHIAVE, altrimenti due minyanim continui si
  // fondono per il motivo sbagliato: `times` e' vuoto in tutti e due, e a 770
  // la mincha (da mincha gedola a shkia) e il maariv (da tzeit a chatzot) sono
  // finiti in una riga sola «Mincha / Maariv» — col maariv sparito dalla
  // pagina. Una fusione vera (stessa finestra in tutti e due) continua a
  // funzionare, perche' allora la chiave coincide davvero.
  const chiaveFinestra = (e) => (e.continuous
    ? `|ogni${e.continuous.every}:${e.continuous.from}>${e.continuous.until}` : '');
  const keyOf = (e) => `${[...(e.days || [])].sort().join(',')}|${[...(e.times || [])].sort().join(',')}|${e.season || ''}${chiaveFinestra(e)}`;
  const mincha = pt.mincha || [];
  const arvit = pt.arvit || [];
  const arvitKeys = new Set(arvit.map(keyOf));
  const combined = mincha.filter((e) => arvitKeys.has(keyOf(e)));
  const combinedKeys = new Set(combined.map(keyOf));
  const minchaOnly = mincha.filter((e) => !combinedKeys.has(keyOf(e)));
  const arvitOnly = arvit.filter((e) => !combinedKeys.has(keyOf(e)));

  return (
    <div style={{ background: 'white', padding: 24, borderRadius: 12, marginTop: 20, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ marginTop: 0, color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 8 }}>
        🕐 {t('Prayer Times')}
        {candleLighting && (
          <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.muted, background: COLORS.bg, padding: '4px 8px', borderRadius: 4 }}>
            {t("This week's candle lighting:")} <strong style={{ color: COLORS.accent }}>{candleLighting}</strong>
          </span>
        )}
        {shabbatEnd && (
          <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.muted, background: COLORS.bg, padding: '4px 8px', borderRadius: 4 }}>
            {t('Shabbat ends:')} <strong style={{ color: COLORS.accent }}>{shabbatEnd}</strong>
          </span>
        )}
      </h3>
      {holidayTimes.length > 0 && (
        <div style={{ marginBottom: 18, padding: '12px 14px', background: '#fffbeb', border: `1px solid ${COLORS.accent}`, borderRadius: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.primaryDark, marginBottom: 6 }}>
            ✨ {t('Holiday times')}
          </div>
          {holidayTimes.map((entry, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', borderBottom: idx < holidayTimes.length - 1 ? `1px dashed ${COLORS.border}` : 'none' }}>
              <span style={{ fontSize: 14, color: COLORS.text, minWidth: 0 }}>
                <strong>{t(entry.occasion || 'Holiday')}</strong>
                {entry.service && <span style={{ color: COLORS.muted }}> · {t(entry.service)}</span>}
                <span style={{ display: 'block', fontSize: 12, color: COLORS.muted }}>
                  {formatHolidayDate(entry._on)}
                  {entry.note && <> · {t(entry.note)}</>}
                </span>
              </span>
              <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.text, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {(entry.times || []).map((tm, i) => (
                  <span key={i}>
                    {formatTime(tm, zmanim)}
                    {i < entry.times.length - 1 && <span style={{ color: COLORS.muted }}> · </span>}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
      {hasStructured ? (
        <>
          {renderService('Shacharit', pt.shacharit)}
          {renderService('Mincha', minchaOnly)}
          {renderService('Mincha / Maariv', combined)}
          {renderService('Kabbalat Shabbat', pt.kabbalat_shabbat)}
          {renderService('Arvit / Maariv', arvitOnly)}
        </>
      ) : null}
      {Array.isArray(pt.notes) && pt.notes.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
          {pt.notes.map((n, i) => <div key={i}>ℹ️ {n}</div>)}
        </div>
      )}
      {pt.raw_text && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: COLORS.muted }}>
            {t('Original text from directory')}
          </summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: COLORS.muted, fontFamily: 'inherit', marginTop: 8, paddingLeft: 12, borderLeft: `3px solid ${COLORS.border}` }}>
            {pt.raw_text}
          </pre>
        </details>
      )}
    </div>
  );
}
